import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { Empty, List, Modal, Spin, Tooltip, Typography } from 'antd'
import { useSetRecoilState } from 'recoil'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { findSimilarNotecards } from '@/api/cb/search'
import { WBSearchResultsNotecard } from 'common/src/elasticSearch/types'
import { FindSimilarNotecardsResponse } from 'common/src/elasticSearch/responses'
import { NewlineText } from '@/components/utils/newlineText'

interface FindSimilarNotecardsModalProps {
    isOpen: boolean
    onClose: () => void
    questionText: string
    solutionText: string
    courseName: string
}

export const FindSimilarNotecardsModal: React.FC<
    FindSimilarNotecardsModalProps
> = (props): ReactElement => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [results, setResults] = useState<FindSimilarNotecardsResponse | null>(
        null
    )
    const setViewingState = useSetRecoilState(viewingStateAtom)

    const fetchResults = useCallback(async () => {
        setIsLoading(true)
        setResults(null)
        const response = await findSimilarNotecards(
            props.questionText,
            props.solutionText,
            props.courseName
        )
        if (!response.data.isError) {
            setResults(response.data.payload)
        }
        setIsLoading(false)
    }, [props.questionText, props.solutionText, props.courseName])

    useEffect(() => {
        if (props.isOpen) {
            fetchResults()
        }
    }, [props.isOpen, fetchResults])

    const handleNotecardClick = useCallback(
        (notecard: WBSearchResultsNotecard) => {
            setViewingState({
                contentPath: notecard.contentPath,
                viewingStateType: ViewingStateType.notecards,
                id: notecard.id,
            })
            props.onClose()
        },
        [props, setViewingState]
    )

    const renderNotecardList = (
        title: string,
        notecards: WBSearchResultsNotecard[]
    ): ReactElement | null => {
        if (!notecards.length) return null
        return (
            <>
                <Typography.Title level={5}>{title}</Typography.Title>
                <List
                    size="small"
                    dataSource={notecards}
                    renderItem={(notecard) => (
                        <List.Item
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleNotecardClick(notecard)}
                        >
                            <Tooltip
                                placement="topLeft"
                                title={
                                    <NewlineText text={notecard.hoverText} />
                                }
                            >
                                <div>{notecard.displayText}</div>
                            </Tooltip>
                        </List.Item>
                    )}
                />
            </>
        )
    }

    const hasNoResults =
        results &&
        !results.questionMatchNotecards.length &&
        !results.solutionMatchNotecards.length

    return (
        <Modal
            title="Similar Notecards"
            open={props.isOpen}
            onCancel={props.onClose}
            footer={null}
            width={600}
        >
            {isLoading && (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Spin size="large" />
                </div>
            )}
            {hasNoResults && <Empty description="No similar notecards found" />}
            {results && !hasNoResults && (
                <>
                    {renderNotecardList(
                        'Matched by question',
                        results.questionMatchNotecards
                    )}
                    {renderNotecardList(
                        'Matched by solution',
                        results.solutionMatchNotecards
                    )}
                </>
            )}
        </Modal>
    )
}
